import * as THREE from 'three';

/* ═══════════════════════════════════════════════════════════════════════════
   Le matériau des projectiles — trois zones concentriques, et un
   anti-aliasing ANALYTIQUE (PLAN.md §4.4).

   Pourquoi analytique plutôt que par texture ou par SMAA :

   La couche danger est rendue APRÈS le SMAA, dans la scène overlay. Elle n'a
   donc aucun anti-aliasing — et sur des sprites petits, rapides et très
   contrastés, cela produit un scintillement en mouvement particulièrement
   désagréable. Le correctif tient dans le shader : on connaît la distance
   exacte au centre, `fwidth` donne la taille d'un pixel en espace UV, et un
   `smoothstep` sur 1,5 pixel donne un bord parfait. Coût nul, résultat
   supérieur au SMAA sur des formes aussi simples — et aucune texture à
   charger, décoder ni transcoder.

   L'injection passe par `onBeforeCompile` sur un MeshBasicMaterial plutôt que
   par un ShaderMaterial : on conserve ainsi toute la gestion de l'instancing de
   three.js, qui est précisément ce qu'on ne veut pas réécrire.
   ═══════════════════════════════════════════════════════════════════════════ */

export interface BulletColors {
  /** Blanc pur : c'est ce noyau qui EST la zone dangereuse (hitbox à 85 %). */
  readonly core: string;
  readonly body: string;
  /** Contour sombre — il rend la balle lisible sur fond clair comme sur fond
   *  sombre sans rien supposer du décor. */
  readonly rim: string;
}

/** Fraction du rayon occupée par le noyau, puis par le corps. */
const CORE_END = 0.45;
const BODY_END = 0.88;

/** Gain émissif HDR appliqué aux SEULS tirs joueur (blending additif).
 *
 *  Retour de jeu : « ça manque d'effet lumineux ». Le tir joueur sortait à sa
 *  valeur de palette, plafonnée à 1,0 : le noyau blanc frôlait tout juste le
 *  seuil de bloom (0,85) et le corps cyan passait dessous. Un tir qui ne franchit
 *  pas le seuil ne rayonne pas — l'arme du joueur, qu'il fixe en permanence,
 *  paraissait donc plate. En poussant la sortie au-dessus de 1,0 dans le
 *  framebuffer HalfFloat (non clampé), les zones vives franchissent le seuil avec
 *  de la marge et le projectile se met VRAIMENT à glower (PLAN.md §4.6, émissif
 *  entre 2,0 et 8,0).
 *
 *  ⚠️ Réservé au blending ADDITIF, c'est-à-dire aux tirs JOUEUR. Les projectiles
 *  ENNEMIS (NormalBlending, couche danger) n'y touchent jamais : ils sont hors
 *  bloom par construction (scène overlay, après toute la chaîne) et leur couleur
 *  doit rester EXACTEMENT celle qui a été autorisée (§4.4). Le gain est donc
 *  injecté conditionnellement, jamais dans le programme des balles ennemies.
 *
 *  Valeur de goût : un aller-retour visuel humain peut la régler dans [2,0 ; 4,0]
 *  — plus haut, un tir nourri empile l'additif et vire à la bouillie blanche. */
const PLAYER_BULLET_EMISSIVE = 3.0;

export function makeBulletMaterial(c: BulletColors, additive: boolean): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    depthWrite: false,
    // ⚠️ depthTest désactivé : la couche danger est toujours au-dessus de tout.
    // Une balle qui disparaît derrière un ennemi est une mort incompréhensible.
    depthTest: false,
    blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
  });

  // Force la varying `vUv` : sans texture, three ne la déclare pas, et le
  // shader injecté ne compilerait pas.
  mat.defines = { ...(mat.defines ?? {}), USE_UV: '' };

  const core = new THREE.Color(c.core);
  const body = new THREE.Color(c.body);
  const rim = new THREE.Color(c.rim);

  // Gain HDR poussé dans la seule variante additive (tir joueur). Vide sur les
  // balles ennemies : leur programme reste identique au pixel près.
  const emissive = additive ? `\n  diffuseColor.rgb *= ${PLAYER_BULLET_EMISSIVE.toFixed(2)};` : '';

  mat.onBeforeCompile = (shader) => {
    shader.uniforms['uCore'] = { value: core };
    shader.uniforms['uBody'] = { value: body };
    shader.uniforms['uRim'] = { value: rim };

    /* ── Passage de la FORME et de la TEINTE par instance ─────────────────
       Deux attributs par-instance posés par `QuadBatch` : `aShape` sélectionne
       la silhouette (orbe / faisceau / disque / missile) et `aTint` module la
       couleur. On les relaie au fragment par des varyings. Constants sur toute
       l'instance, ils ne divergent jamais au sein d'une primitive — le
       branchement `if (vShape ...)` du fragment est donc uniforme et gratuit. */
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
attribute float aShape;
attribute float aTint;
varying float vShape;
varying float vTint;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
  vShape = aShape;
  vTint = aTint;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
uniform vec3 uCore;
uniform vec3 uBody;
uniform vec3 uRim;
varying float vShape;
varying float vTint;`,
      )
      /* ⚠️ POINT D'INJECTION : avant `<specularmap_fragment>`, et surtout PAS
         avant `<opaque_fragment>`.

         Dans le shader de MeshBasicMaterial, l'enchaînement est :

             reflectedLight.indirectDiffuse *= diffuseColor.rgb;
             vec3 outgoingLight = reflectedLight.indirectDiffuse;
             #include <envmap_fragment>
             #include <opaque_fragment>       ← trop tard

         `outgoingLight` est donc figé AVANT `<opaque_fragment>`. Injecter là
         laissait passer l'alpha — qui vient bien de `diffuseColor.a` — mais pas
         la couleur : les projectiles gardaient la forme correcte et sortaient
         intégralement blancs, celle du matériau. Panne discrète, parce que le
         shader compile parfaitement et que la géométrie a l'air juste. */
      .replace(
        '#include <specularmap_fragment>',
        `#include <specularmap_fragment>
{
  // Coordonnée centrée sur le quad : (0,0) au centre, ±0,5 aux bords.
  vec2 uv = vUv - 0.5;
  // Distance radiale normalisée : 0 au centre, 1 au bord.
  float d = length(uv) * 2.0;

  // fwidth() donne la variation de d sur un pixel écran. C'est ce qui rend
  // l'anti-aliasing indépendant de la taille à l'écran et du resolution
  // scale — le bord reste net à 0,5 comme à 1,0.
  float fw = max(fwidth(d), 1e-5) * 1.5;

  vec3 zone = uBody;
  float alpha = 0.0;

  if (vShape < 0.5) {
    // ── ORBE (forme 0) : le rendu d'origine, à zones concentriques. ────────
    zone = mix(uCore, uBody, smoothstep(${CORE_END.toFixed(2)} - fw, ${CORE_END.toFixed(2)} + fw, d));
    zone = mix(zone, uRim, smoothstep(${BODY_END.toFixed(2)} - fw, ${BODY_END.toFixed(2)} + fw, d));
    alpha = 1.0 - smoothstep(1.0 - fw, 1.0 + fw, d);
  } else if (vShape < 1.5) {
    // ── FAISCEAU LASER (forme 1) : une BARRE verticale, pas un point. ──────
    // La section utile est HORIZONTALE (vUv.x) ; la longueur (vUv.y) file le
    // long du rayon. Noyau blanc central, corps coloré, bord net.
    float cx = abs(uv.x) * 2.0;
    float fwx = max(fwidth(cx), 1e-5) * 1.5;
    zone = mix(uCore, uBody, smoothstep(0.30 - fwx, 0.30 + fwx, cx));
    zone = mix(zone, uRim, smoothstep(0.82 - fwx, 0.82 + fwx, cx));
    // Crépitement ÉLECTRIQUE : une ondulation fine le long de la lame. Elle est
    // statique dans le shader — l'ANIMATION vient du scintillement de teinte que
    // View applique par segment (vTint), et les deux ensemble « grésillent ».
    zone *= 0.88 + 0.12 * sin(vUv.y * 130.0);
    // Bord horizontal net ; extrémités haute/basse à peine adoucies pour que les
    // segments empilés se fondent en une colonne continue, sans pointillé.
    float aX = 1.0 - smoothstep(1.0 - fwx, 1.0 + fwx, cx);
    float aY = 1.0 - smoothstep(0.96, 1.02, abs(uv.y) * 2.0);
    alpha = aX * aY;
  } else if (vShape < 2.5) {
    // ── DISQUE TRANCHANT (forme 2) : un anneau DENTÉ. Il tourne via la ─────
    // matrice d'instance (View), donc les dents balaient : une scie qui file.
    float ang = atan(uv.y, uv.x);
    float teeth = 0.5 + 0.5 * cos(ang * 8.0);   // huit dents
    float rim = 0.80 + 0.14 * teeth;            // bord découpé en scie
    // Moyeu sombre, couronne vive, liseré au noyau sur le tranchant.
    zone = mix(uRim, uBody, smoothstep(0.28 - fw, 0.28 + fw, d));
    zone = mix(zone, uCore, smoothstep(rim - 0.14, rim - 0.02, d));
    alpha = 1.0 - smoothstep(rim - fw, rim + fw, d);
    // Centre ÉVIDÉ : on lit un disque/anneau, jamais un gros point.
    alpha *= smoothstep(0.14, 0.28, d);
  } else {
    // ── MISSILE GUIDÉ (forme 3) : un DARD orienté sur sa vitesse. ──────────
    // Le quad est tourné pour que +y pointe dans le sens du mouvement (View).
    // vUv.y : 0 = queue (traînée), 1 = tête. La demi-largeur s'effile vers la
    // queue → une ogive, pas une capsule.
    float head = uv.y;
    float taper = mix(0.10, 0.42, smoothstep(-0.5, 0.35, head));
    float fwx = max(fwidth(uv.x), 1e-5) * 1.5;
    float body = 1.0 - smoothstep(taper - fwx, taper + fwx, abs(uv.x));
    // Tête chaude (noyau) vers l'avant ; corps coloré ; traînée qui s'éteint.
    zone = mix(uBody, uCore, smoothstep(0.0, 0.42, head));
    float tail = smoothstep(-0.5, -0.05, head);
    alpha = body * tail;
  }

  diffuseColor.rgb = zone * vTint;${emissive}
  diffuseColor.a *= alpha;
}`,
      );
  };

  // ⚠️ Sans clé de cache distincte, three réutiliserait le programme déjà
  // compilé pour un MeshBasicMaterial ordinaire et l'injection serait
  // silencieusement ignorée — le symptôme le plus déroutant de three.js.
  // La clé inclut les couleurs : deux familles produisent deux programmes,
  // ce qui est voulu et borné (trois familles ennemies + une joueur).
  // ⚠️ v2 : le shader porte désormais le branchement de FORME (orbe/faisceau/
  // disque/missile) et les varyings `vShape`/`vTint`. Le suffixe force three à
  // recompiler au lieu de réutiliser le programme v1 déjà en cache.
  const key = `bullet-v2-${c.core}${c.body}${c.rim}-${additive ? 'add' : 'norm'}`;
  mat.customProgramCacheKey = () => key;

  return mat;
}
